import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import GlassCard from '../ui/GlassCard';
import { track } from '../../lib/analytics';
import { useSectionView } from '../../hooks/useSectionView';

// Served by api/metrics.js, which holds the Dune key server-side and is cached for an hour.
const METRICS_URL = '/api/metrics';

// Figures are rounded down, never up: "4.8K+" for 4,800, not "5K+".
const abbreviate = (num) => {
  if (num >= 1000000) return `${Math.floor(num / 100000) / 10}M+`;
  if (num >= 10000) return `${Math.floor(num / 1000)}K+`;
  if (num >= 1000) return `${Math.floor(num / 100) / 10}K+`;
  return Math.floor(num).toLocaleString();
};

const formatValue = (value, type) => {
  if (value === null || value === undefined) return '—';

  const num = parseFloat(value);
  if (Number.isNaN(num)) return '—';

  if (type === 'currency') return `$${abbreviate(num)}`;
  if (type === 'number') return abbreviate(num);

  return value;
};

// Labels are stored as translation keys, not resolved strings, so the cards
// re-render in the new language when the user switches locale mid-session.
const METRIC_LABEL_KEYS = ['metrics.totalVolume', 'metrics.volume24h', 'metrics.totalAccounts'];

const buildMetrics = (values, loading = false) =>
  METRIC_LABEL_KEYS.map((labelKey, index) => ({
    labelKey,
    value: values[index],
    loading
  }));

// Last updated from Dune on 2026-10-21. There is no meaningful fallback for the 24h figure.
const FALLBACK_VALUES = ['$57.8M+', '—', '4.8K+'];

export default function Metrics() {
  const { t } = useTranslation();
  const { darkMode } = useTheme();
  const sectionRef = useSectionView('metrics', 7);
  const [metrics, setMetrics] = useState(() => buildMetrics(['—', '—', '—'], true));

  const [live, setLive] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch(METRICS_URL);
        if (!response.ok) {
          track('metrics_fallback_shown', { reason: 'fetch_error' });
          throw new Error(`Metrics endpoint returned ${response.status}`);
        }
        const data = await response.json();

        setMetrics(buildMetrics([
          formatValue(data.totalVolumeUsd, 'currency'),
          formatValue(data.volume24hUsd, 'currency'),
          formatValue(data.totalAccounts, 'number')
        ]));
        setLive(true);
      } catch (error) {
        // Locally (no serverless functions) or on a Dune failure, show the fallback figures
        // without claiming they are live.
        track('metrics_fallback_shown', { reason: error.message || 'missing' });
        setMetrics(buildMetrics(FALLBACK_VALUES));
      }
    };

    fetchMetrics();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="metrics"
      className={`py-24 ${darkMode ? 'bg-main-black' : 'bg-white'}`}
    >
      <div className="mainCont">
        {/* Headline */}
        <h2 className={`titleFont text-3xl md:text-4xl font-bold text-center mb-12 ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {t('metrics.headline')}
        </h2>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
          {metrics.map((metric, index) => (
            <GlassCard
              key={metric.labelKey}
              padding="lg"
              className="text-center"
            >
              {/* Value */}
              <div className={`titleFont text-3xl md:text-4xl lg:text-5xl font-bold mb-2 ${
                metric.loading ? 'animate-pulse' : ''
              } ${
                index % 2 === 0
                  ? (darkMode ? 'text-main' : 'text-purple-600')
                  : (darkMode ? 'text-main-orange' : 'text-orange-500')
              }`}>
                {metric.value}
              </div>

              {/* Label */}
              <div className={`text-sm md:text-base font-medium ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t(metric.labelKey)}
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Subtle note, only when the figures came from Dune */}
        {live && (
          <p className={`text-center text-sm mt-8 ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            {t('metrics.note')}
          </p>
        )}
      </div>
    </section>
  );
}
